import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { fetchSites } from "../../services/apiService";
import { fetchSitesByPlanFilter } from "../../services/pluginPlanService";
import { useNavigate, useParams } from "react-router";
import { useTelemetryContext } from "../../contexts/TelemetryContext";
import TablePagination from "../reused-components/TablePagination";
import {
    usePersistedPagination,
    useResetPageOnFilterChange,
} from "../../hooks/usePersistedPagination";
import PlanBadge from "../reused-components/PlanBadge";
import { useProductSlugs } from "../../hooks/useProductSlugs";
import { useSitePlanTiers } from "../../hooks/useSitePlanTiers";
import { PLAN_TIER } from "../../utils/pluginPlan";

function SitesTable({
    paginationPerPage,
    searchTerm,
    userId = null,
    stateFilter = "",
    planFilter = "",
    uninstallReason = null,
    onlyProduction = false,
    onDataLoad,
}) {
    const [data, setData] = useState([]);
    const [planFilteredSites, setPlanFilteredSites] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const { storeId, productId } = useParams();
    const storageKey = `sites-${productId || "default"}-${planFilter || "all"}`;
    const {
        currentPage,
        perPage,
        setCurrentPage,
        setPerPage,
        resetToFirstPage,
        paginationDefaultPage,
    } = usePersistedPagination(storageKey, paginationPerPage || 10);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const { selectedProduct } = useTelemetryContext();
    const { freeSlug, proSlug } = useProductSlugs();
    const isPlanFilter = planFilter === PLAN_TIER.PRO || planFilter === PLAN_TIER.FREE;
    const serverPlanBySiteId = useSitePlanTiers(isPlanFilter ? [] : data, freeSlug, proSlug);
    const navigate = useNavigate();

    useResetPageOnFilterChange(resetToFirstPage, [
        searchTerm,
        stateFilter,
        planFilter,
        uninstallReason,
        onlyProduction,
    ]);

    const fetchData = async (page, rowsPerPage) => {
        setFetching(true);
        try {
            const responseData = await fetchSites(
                selectedProduct.id,
                page,
                rowsPerPage,
                userId,
                searchTerm,
                stateFilter || null,
                uninstallReason,
                onlyProduction
            );

            const rows = responseData.data || [];
            const total = responseData.total || 0;
            setData(rows);
            setTotalRows(total);
            if (onDataLoad) onDataLoad({ data: rows, total });
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Failed to load data. Please try again.");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (!selectedProduct || !productId || selectedProduct.id.toString() !== productId) {
            return;
        }

        if (isPlanFilter) {
            return;
        }

        fetchData(currentPage, perPage);
    }, [
        selectedProduct,
        productId,
        currentPage,
        perPage,
        searchTerm,
        stateFilter,
        uninstallReason,
        onlyProduction,
        isPlanFilter,
        userId,
    ]);

    useEffect(() => {
        if (!selectedProduct || !productId || selectedProduct.id.toString() !== productId) {
            return undefined;
        }

        if (!isPlanFilter || !freeSlug) {
            setPlanFilteredSites([]);
            return undefined;
        }

        let cancelled = false;
        setFetching(true);

        fetchSitesByPlanFilter({
            productId: selectedProduct.id,
            planFilter,
            freeSlug,
            proSlug,
            userId,
            searchTerm,
            stateFilter,
            uninstallReason,
            onlyProduction,
        })
            .then((result) => {
                if (cancelled) {
                    return;
                }

                setPlanFilteredSites(result.data);
                setTotalRows(result.total);
                resetToFirstPage();
                if (onDataLoad) {
                    onDataLoad({ data: result.data, total: result.total });
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Error fetching sites by plan:", error);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setFetching(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        selectedProduct,
        productId,
        searchTerm,
        stateFilter,
        planFilter,
        uninstallReason,
        onlyProduction,
        isPlanFilter,
        freeSlug,
        proSlug,
        userId,
    ]);

    const displayData = useMemo(() => {
        if (!isPlanFilter) {
            return data;
        }

        const start = (currentPage - 1) * perPage;
        return planFilteredSites.slice(start, start + perPage);
    }, [isPlanFilter, data, planFilteredSites, currentPage, perPage]);

    const planBySiteId = useMemo(() => {
        if (isPlanFilter) {
            return Object.fromEntries(
                planFilteredSites.map((site) => [site.id, planFilter])
            );
        }

        return serverPlanBySiteId;
    }, [isPlanFilter, planFilteredSites, planFilter, serverPlanBySiteId]);

    const handleSiteDetailClick = (siteId) => {
        navigate(`/stores/${storeId}/plugins/${productId}/sites/${siteId}`);
    };

    const handleUserDetailClick = (userId) => {
        navigate(`/stores/${storeId}/plugins/${productId}/users/${userId}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        resetToFirstPage();
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                name: "ID",
                selector: (row) => row.id,
                sortable: true,
                cell: (row) => (
                    <span className="id-span" onClick={() => handleSiteDetailClick(row.id)}>
                        {row.id}
                    </span>
                ),
            },
            {
                name: "Address",
                selector: (row) => row.address,
                sortable: true,
                cell: (row) => (
                    <span
                        onClick={() => handleSiteDetailClick(row.id)}
                        className="link-icon site-address-cell"
                    >
                        {row.address}
                        <PlanBadge tier={planBySiteId[row.id]} />
                    </span>
                ),
            },
            {
                name: "Installed at",
                selector: (row) => row.installed_at,
                sortable: true,
                cell: (row) =>
                    row.installed_at
                        ? new Date(row.installed_at).toLocaleDateString()
                        : "N/A",
            },
            {
                name: "User ID",
                selector: (row) => row.user_id,
                sortable: true,
                cell: (row) => (
                    <span className="link-icon" onClick={() => handleUserDetailClick(row.user_id)}>
                        {row.user_id}
                    </span>
                ),
            },
            {
                name: "SDK Version",
                selector: (row) => row.sdk_version,
                sortable: true,
            },
            {
                name: "Version",
                selector: (row) => row.version,
                sortable: true,
            },
        ];

        if (stateFilter === "deactivate" || stateFilter === "uninstall") {
            baseColumns.push({
                name: "Reason Detail",
                selector: (row) => row.reason_detail,
                sortable: true,
                cell: (row) => row.reason_detail || "N/A",
            });
        }

        return baseColumns;
    }, [stateFilter, planBySiteId]);

    return (
        <div>
            {loading ? (
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={displayData}
                    pagination
                    paginationServer={!isPlanFilter}
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationDefaultPage={paginationDefaultPage}
                    paginationComponent={TablePagination}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    responsive
                    progressPending={fetching}
                    progressComponent={(
                        <div className="spinner-container">
                            <div className="spinner"></div>
                        </div>
                    )}
                    highlightOnHover
                    className="custom-data-table"
                />
            )}
        </div>
    );
}

export default SitesTable;

